/**
 * Recursive AND/OR group for the CALC wizard. Holds an ordered list of child
 * nodes (comparison rows, NOT wrappers, or nested groups) combined with a
 * single operator the user toggles between AND and OR.
 */
import React from "react"
import { Segmented, Button, Tooltip } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  CalcGroup,
  CalcNode,
  emptyGroup,
  placeholder,
} from "./calcExpression"
import CalcConditionRow from "./CalcConditionRow.component"

interface CalcConditionGroupProps {
  node: CalcGroup
  onChange: (next: CalcNode) => void
  /** Omitted on the root group (which can't be deleted). */
  onDelete?: () => void
  depth?: number
}

const CalcConditionGroup: React.FC<CalcConditionGroupProps> = ({
  node,
  onChange,
  onDelete,
  depth = 0,
}) => {
  const replaceChild = (index: number, next: CalcNode): void => {
    onChange({
      ...node,
      children: node.children.map((c, i) => (i === index ? next : c)),
    })
  }
  const removeChild = (index: number): void => {
    onChange({
      ...node,
      children: node.children.filter((_, i) => i !== index),
    })
  }
  const addCondition = (): void => {
    onChange({ ...node, children: [...node.children, placeholder()] })
  }
  const addGroup = (): void => {
    // Nested group defaults to the opposite operator — the usual reason to
    // nest is to mix AND with OR.
    const childOp = node.op === "AND" ? "OR" : "AND"
    onChange({ ...node, children: [...node.children, emptyGroup(childOp)] })
  }

  return (
    <div className="qc-calc-group" data-depth={depth}>
      <div className="qc-calc-group__head">
        <Segmented<"AND" | "OR">
          size="small"
          value={node.op}
          onChange={(op) => onChange({ ...node, op })}
          options={[
            { label: "AND (all true)", value: "AND" },
            { label: "OR (any true)", value: "OR" },
          ]}
        />
        <span className="qc-calc-group__count">
          {node.children.length} condition
          {node.children.length === 1 ? "" : "s"}
        </span>
        {onDelete && (
          <Tooltip title="Remove group">
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={onDelete}
            />
          </Tooltip>
        )}
      </div>

      <div className="qc-calc-group__children">
        {node.children.map((child, idx) => {
          const wrapInNot = (): void =>
            replaceChild(idx, { kind: "not", child })
          if (child.kind === "group") {
            return (
              <CalcConditionGroup
                key={idx}
                node={child}
                depth={depth + 1}
                onChange={(next) => replaceChild(idx, next)}
                onDelete={() => removeChild(idx)}
              />
            )
          }
          if (child.kind === "not") {
            const inner = child.child
            const unwrap = (): void => replaceChild(idx, inner)
            return (
              <div className="qc-calc-not" key={idx}>
                <Tooltip title="Remove the NOT (un-negate)">
                  <Button size="small" onClick={unwrap}>
                    NOT
                  </Button>
                </Tooltip>
                {inner.kind === "group" ? (
                  <CalcConditionGroup
                    node={inner}
                    depth={depth + 1}
                    onChange={(next) =>
                      replaceChild(idx, { kind: "not", child: next })
                    }
                    onDelete={() => removeChild(idx)}
                  />
                ) : (
                  <CalcConditionRow
                    node={
                      inner.kind === "cmp" || inner.kind === "placeholder"
                        ? inner
                        : placeholder()
                    }
                    onChange={(next) =>
                      replaceChild(idx, { kind: "not", child: next })
                    }
                    onDelete={() => removeChild(idx)}
                  />
                )}
              </div>
            )
          }
          // cmp or placeholder
          return (
            <CalcConditionRow
              key={idx}
              node={child}
              onChange={(next) => replaceChild(idx, next)}
              onDelete={() => removeChild(idx)}
              onWrapInNot={wrapInNot}
            />
          )
        })}
      </div>

      <div className="qc-calc-group__actions">
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addCondition}
        >
          Add condition
        </Button>
        <Button size="small" type="dashed" onClick={addGroup}>
          Add group ({node.op === "AND" ? "OR" : "AND"})
        </Button>
      </div>
    </div>
  )
}

export default CalcConditionGroup
